import React, { useState, useRef } from 'react';
import styles from './style.module.css';
import Typography from '../../components/Typography/Typography';
import FlatButtons from '../../components/FlatButton/FlatButton';
import { RefreshIcon, StoreCloudUpload } from '../../assets/svgs';

interface Create3DModelProps {
    // onClose?: () => void;
    onModelGenerated?: (modelData: any) => void;
}

export const Create3DModelFromImage: React.FC<Create3DModelProps> = ({
    // onClose,
    onModelGenerated
}) => {
    const [currentStep, setCurrentStep] = useState<'upload' | 'uploaded' | 'generating' | 'completed'>('upload');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [generationProgress, setGenerationProgress] = useState(0);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (file: File) => {
        if (file && (file.type === 'image/jpeg' || file.type === 'image/png')) {
            setUploadedFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
                setCurrentStep('uploaded');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleGenerate = () => {
        setCurrentStep('generating');
        setGenerationProgress(0);

        const interval = setInterval(() => {
            setGenerationProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setCurrentStep('completed');
                    return 100;
                }
                return prev + 10;
            });
        }, 500);
    };

    const handleUseModel = () => {
        if (onModelGenerated) {
            onModelGenerated({
                originalImage: imagePreview,
                modelData: 'generated-3d-model-data',
                fileName: uploadedFile?.name
            });
        }
    };

    const handleRefresh = () => {
        setUploadedFile(null);
        setImagePreview('');
        setCurrentStep('upload');
        setGenerationProgress(0);
    };

    return (
        <div className={styles.modalContainer}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <Typography
                        variant='heading5'
                        fontFamily='satoshi'
                        weight='bold'
                        className={styles.modalTitle}>
                        Create 3D Model From Image
                    </Typography>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.contentArea}>
                        <div className={styles.leftSection}>
                            {currentStep === 'upload' && (
                                <div className={styles.imageSection}>
                                    <div
                                        className={`${styles.uploadArea} ${isDragOver ? styles.dragOver : ''}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            title='image-file'
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/jpeg,image/png"
                                            onChange={handleFileChange}
                                            className={styles.fileInput}
                                            id="model-image-upload"
                                        />
                                        <label htmlFor="model-image-upload" className={styles.uploadLabel}>
                                            <div className={styles.uploadIcon}>
                                                <img
                                                    src={StoreCloudUpload}
                                                    alt='upload icon'
                                                />
                                            </div>
                                            <Typography
                                                variant='bodyRegular'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                className={styles.uploadText}>
                                                Drag and drop files here
                                            </Typography>
                                            <Typography
                                                variant='bodyRegular'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                className={styles.uploadSubtext}>
                                                or
                                            </Typography>
                                            <FlatButtons
                                                type="button"
                                                className={styles.browseButton}
                                                onClick={handleBrowseClick}
                                            >
                                                <Typography
                                                    variant='buttonText'
                                                    fontFamily='satoshi'
                                                    weight='regular'
                                                    size='base'
                                                    className={styles.browseButtonText}>
                                                    Browse
                                                </Typography>
                                            </FlatButtons>
                                            <Typography
                                                variant='bodyRegular'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                className={styles.supportedFormats}>
                                                Supported: JPG, PNG
                                            </Typography>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {(currentStep === 'uploaded' || currentStep === 'generating' || currentStep === 'completed') && (
                                <div className={styles.imageSection}>
                                    <div className={styles.imagePreview}>
                                        <img
                                            src={imagePreview}
                                            alt="Uploaded preview"
                                            className={styles.previewImage}
                                        />
                                    </div>
                                    <div className={styles.imageInfo}>
                                        <Typography
                                            variant='bodySmall'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            className={styles.fileName}>
                                            {uploadedFile?.name}
                                        </Typography>
                                        <button
                                        type='button'
                                            onClick={handleRefresh}
                                            className={styles.refreshBtn}>
                                            <img
                                                src={RefreshIcon}
                                                alt='refresh button'
                                                className={styles.refreshIcon}
                                            />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className={styles.buttonSection}>
                                <FlatButtons
                                    onClick={handleGenerate}
                                    disabled={currentStep !== 'uploaded'}
                                    className={styles.generateButton}>
                                    <Typography
                                        variant='buttonText'
                                        fontFamily='satoshi'
                                        weight='regular'
                                        className={styles.generateButtonText}>
                                        Generate
                                    </Typography>
                                </FlatButtons>
                            </div>
                        </div>

                        {(currentStep === 'generating' || currentStep === 'completed') && (
                            <div className={styles.rightSection}>
                                <div className={styles.generationSection}>
                                    <Typography
                                        variant='bodyRegular'
                                        fontFamily='satoshi'
                                        weight='regular'
                                        className={styles.generationText}>
                                        {currentStep === 'generating'
                                            ? 'Generating 3D model of your Product'
                                            : 'The 3D Model of Your Product is Ready'
                                        }
                                    </Typography>

                                    <div className={styles.progressBar}>
                                        <div
                                            className={styles.progressFill}
                                            style={{ width: `${generationProgress}%` }}
                                        ></div>
                                    </div>

                                    <div className={styles.modelPreviewArea}>
                                        {currentStep === 'generating' && (
                                            <div className={styles.loadingContainer}>
                                                <div className={styles.loadingSpinner}></div>
                                            </div>
                                        )}

                                        {currentStep === 'completed' && (
                                            <div className={styles.completedContainer}>
                                                <img
                                                    src={imagePreview}
                                                    alt="3D Model preview"
                                                    className={styles.modelImage}
                                                />
                                                <Typography
                                                    variant='bodySmall'
                                                    fontFamily='satoshi'
                                                    weight='regular'
                                                    className={styles.interactionText}>
                                                    Click and drag to rotate
                                                </Typography>
                                            </div>
                                        )}
                                    </div>

                                    {currentStep === 'completed' && (
                                        <div className={styles.actionSection}>
                                            <FlatButtons
                                                onClick={handleUseModel}
                                                className={styles.useModelButton}>
                                                <Typography
                                                    variant='buttonText'
                                                    fontFamily='satoshi'
                                                    weight='regular'
                                                    className={styles.useModelButtonText}>
                                                    Use 3D model for product upload
                                                </Typography>
                                            </FlatButtons>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};